import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, Hourglass, XCircle, GraduationCap, Briefcase, Certificate,
  CurrencyInr, FilePdf, ArrowSquareOut, WhatsappLogo, EnvelopeSimple, Copy,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { BACKEND_URL } from "../lib/api";

const STAGES = [
  { id: "submitted", label: "Submitted" },
  { id: "approved", label: "Approved" },
  { id: "enrolled", label: "Enrolled" },
  { id: "studying", label: "Studying" },
  { id: "placement", label: "Placement" },
  { id: "completed", label: "Completed" },
];

function deriveStageIndex(d) {
  if (d.status === "rejected") return -1;
  if (d.status === "pending") return 0;
  if (d.status === "approved") return 1;
  // enrolled — pick the deepest progress
  const map = { not_started: 2, studying: 3, placement: 4, completed: 5 };
  return map[d.progress] ?? 2;
}

export default function TrackPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${BACKEND_URL}/api/registrations/track/${token}`;
    axios.get(url).then((r) => setData(r.data)).catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"));
  }, [token]);

  if (error === "not_found") {
    return (
      <Centered>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-3">Application not found.</h1>
        <p className="text-slate-500 max-w-md">The link is incorrect or has expired. If you believe this is a mistake, contact the registrar.</p>
        <Link to="/" className="sms-btn-secondary mt-6 inline-flex items-center gap-2">Return home</Link>
      </Centered>
    );
  }
  if (error) {
    return <Centered><div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-slate-400">Could not load. Please try again.</div></Centered>;
  }
  if (!data) {
    return <Centered><div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-slate-400">Loading…</div></Centered>;
  }

  const idx = deriveStageIndex(data);
  const rejected = data.status === "rejected";
  const trackUrl = `${window.location.origin}/track/${token}`;
  const certUrl = data.placement?.certificate_token
    ? `${BACKEND_URL}/api/placements/cert/${data.placement.certificate_token}/pdf`
    : null;
  const formPdfUrl = `${BACKEND_URL}/api/registrations/form/${token}/pdf`;
  const payUrl = data.payment?.token ? `/pay/${data.payment.token}` : null;

  const copyTrackingLink = async () => {
    await navigator.clipboard.writeText(trackUrl);
    toast.success("Tracking link copied");
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />

      {/* Branded header */}
      <header className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.institute?.logo_base64 && (
              <img src={data.institute.logo_base64} alt={`${data.institute.name} logo`} className="w-9 h-9 object-contain border border-slate-200 bg-white p-1" />
            )}
            <div>
              <div className="font-display font-black tracking-tight">{data.institute?.name || "Student Management System"}</div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Student Progress Portal</div>
            </div>
          </div>
          <button onClick={copyTrackingLink} data-testid="copy-tracking-link" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)] inline-flex items-center gap-1">
            <Copy size={11} weight="bold" /> Copy link
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-primary)] mb-3">Application Tracker</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter" data-testid="track-name">
            {data.full_name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
            {data.student_id && <span className="font-mono-sms" data-testid="track-student-id">{data.student_id}</span>}
            {data.course?.name && <span>· {data.course.name}{data.course.code ? ` (${data.course.code})` : ""}</span>}
            {data.phone_last4 && <span className="font-mono-sms">· phone ····{data.phone_last4}</span>}
          </div>
        </div>

        {/* Rejection state */}
        {rejected && (
          <div className="border border-[var(--sms-danger)]/30 bg-[var(--sms-danger)]/5 p-5 mb-8" data-testid="track-rejected">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-danger)] mb-2 inline-flex items-center gap-2">
              <XCircle size={12} weight="duotone" /> Application not approved
            </div>
            {data.rejection_reason && <div className="text-sm text-slate-700">"{data.rejection_reason}"</div>}
            <div className="text-xs text-slate-500 mt-2">Decided on {new Date(data.decided_at).toLocaleDateString()}</div>
          </div>
        )}

        {/* Stage stepper */}
        {!rejected && (
          <div className="border border-slate-200 p-6 mb-8" data-testid="track-stepper">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-5">Progress</div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {STAGES.map((s, i) => {
                const reached = i <= idx;
                const isCurrent = i === idx;
                return (
                  <div key={s.id} className={`relative flex flex-col items-start gap-2 ${reached ? "" : "opacity-40"}`} data-testid={`stage-${s.id}`}>
                    <div className={`w-7 h-7 flex items-center justify-center border-2 ${isCurrent ? "border-[var(--sms-primary)] bg-[var(--sms-primary)] text-white" : reached ? "border-[var(--sms-success)] bg-[var(--sms-success)] text-white" : "border-slate-200 bg-white text-slate-300"}`}>
                      {reached ? <CheckCircle size={14} weight="bold" /> : <span className="font-mono-sms text-[10px]">{i + 1}</span>}
                    </div>
                    <div className={`text-xs font-medium ${isCurrent ? "text-[var(--sms-primary)]" : ""}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-400 mt-5">
              Submitted {new Date(data.submitted_at).toLocaleDateString()}
              {data.decided_at && ` · Approved ${new Date(data.decided_at).toLocaleDateString()}`}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Application copy */}
          <Card icon={FilePdf} title="Application copy" testid="card-application">
            <p className="text-sm text-slate-600 mb-3">A PDF copy of what you submitted, including your photo, signature, and uploaded documents.</p>
            <a href={formPdfUrl} target="_blank" rel="noopener noreferrer" data-testid="track-download-form" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
              <FilePdf size={12} weight="duotone" /> Download PDF
            </a>
          </Card>

          {/* Payment */}
          <Card icon={CurrencyInr} title="Tuition payment" testid="card-payment">
            <PaymentBlock data={data} payUrl={payUrl} />
          </Card>

          {/* Placement */}
          {data.placement && (
            <Card icon={Briefcase} title="Placement" testid="card-placement" colSpan={2}>
              <PlacementBlock placement={data.placement} certUrl={certUrl} student={data.full_name} />
            </Card>
          )}

          {/* Certificate */}
          {certUrl && (
            <Card icon={Certificate} title="Placement certificate" testid="card-certificate" colSpan={2}>
              <CertificateBlock certUrl={certUrl} studentName={data.full_name} companyName={data.placement?.company?.name} />
            </Card>
          )}
        </div>

        {/* Contact footer */}
        {(data.institute?.phone || data.institute?.email) && (
          <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500">
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Need help?</div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {data.institute.phone && <a href={`tel:${data.institute.phone}`} className="hover:text-[var(--sms-primary)]">{data.institute.phone}</a>}
              {data.institute.email && <a href={`mailto:${data.institute.email}`} className="hover:text-[var(--sms-primary)]">{data.institute.email}</a>}
              {data.institute.address && <span>{data.institute.address}</span>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6">
      {children}
    </div>
  );
}

function Card({ icon: Icon, title, testid, children, colSpan }) {
  return (
    <div className={`border border-slate-200 p-6 ${colSpan === 2 ? "md:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} weight="duotone" className="text-[var(--sms-primary)]" />
        <div className="font-mono-sms text-xs uppercase tracking-[0.2em]">{title}</div>
      </div>
      {children}
    </div>
  );
}

function PaymentBlock({ data, payUrl }) {
  const map = {
    not_requested: { label: "Not yet requested", icon: Hourglass, color: "text-slate-500" },
    requested: { label: "Awaiting your payment", icon: Hourglass, color: "text-amber-600" },
    claimed: { label: "Verifying your payment…", icon: Hourglass, color: "text-amber-600" },
    verified: { label: "Payment verified · enrolled", icon: CheckCircle, color: "text-[var(--sms-success)]" },
  };
  const m = map[data.payment?.status] || map.not_requested;
  const Icon = m.icon;
  const net = Math.max(0, (data.payment?.amount || 0) - (data.payment?.discount_amount || 0));
  return (
    <>
      <div className={`inline-flex items-center gap-2 text-sm font-medium mb-3 ${m.color}`} data-testid="track-payment-status">
        <Icon size={14} weight="bold" /> {m.label}
      </div>
      {data.payment?.amount > 0 && (
        <div className="text-xs text-slate-500 mb-3">
          Net due: <span className="font-mono-sms text-slate-900">₹ {Number(net).toLocaleString("en-IN")}</span>
          {data.payment.discount_amount > 0 && <span className="ml-2 text-[var(--sms-primary)]">(₹{Number(data.payment.discount_amount).toLocaleString("en-IN")} discount applied)</span>}
        </div>
      )}
      {data.installments?.length > 0 && (
        <div className="text-xs space-y-1 mb-3">
          {data.installments.map((i) => (
            <div key={i.number} className="flex items-center justify-between border-b border-slate-100 py-1.5" data-testid={`track-instalment-${i.number}`}>
              <span>#{i.number} · due {i.due_date}</span>
              <span className="font-mono-sms">
                ₹{Number(i.amount).toLocaleString("en-IN")}
                <span className={`ml-2 ${i.status === "verified" ? "text-[var(--sms-success)]" : "text-slate-400"}`}>
                  · {i.status === "verified" ? "paid" : i.status}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      {payUrl && data.payment?.status !== "verified" && (
        <a href={payUrl} data-testid="track-pay-now" className="sms-btn-primary text-xs inline-flex items-center gap-1">
          <ArrowSquareOut size={12} weight="bold" /> Pay now
        </a>
      )}
    </>
  );
}

function PlacementBlock({ placement, certUrl, student: _student }) {
  const map = {
    active: { label: "On placement", color: "text-amber-600" },
    under_review: { label: "Completion under review", color: "text-amber-700" },
    approved: { label: "Placement completed", color: "text-[var(--sms-success)]" },
    rejected: { label: "Completion rejected — pending re-submit", color: "text-[var(--sms-danger)]" },
  };
  const m = map[placement.status] || { label: placement.status, color: "text-slate-500" };
  return (
    <div className="grid sm:grid-cols-3 gap-6">
      <div className="sm:col-span-1 flex items-start gap-3">
        {placement.company?.logo_base64 ? (
          <img src={placement.company.logo_base64} alt={placement.company.name} className="w-14 h-14 object-contain border border-slate-200 bg-white p-1" />
        ) : (
          <div className="w-14 h-14 border border-slate-200 bg-slate-50 flex items-center justify-center">
            <Briefcase size={22} weight="duotone" className="text-slate-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-black tracking-tight" data-testid="track-company-name">{placement.company?.name || "—"}</div>
          {placement.company?.website && (
            <a href={placement.company.website} target="_blank" rel="noopener noreferrer" className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline">Website</a>
          )}
        </div>
      </div>
      <div className="sm:col-span-2 space-y-2 text-sm">
        <div className={`inline-flex items-center gap-2 font-medium ${m.color}`} data-testid={`placement-status-${placement.status}`}>
          {placement.status === "approved" ? <CheckCircle size={14} weight="bold" /> : <Hourglass size={14} weight="bold" />} {m.label}
        </div>
        <div className="text-slate-600 text-xs">
          {placement.role_title ? <span>Role: <span className="text-slate-900">{placement.role_title}</span> · </span> : null}
          Period: <span className="font-mono-sms">{placement.start_date} → {placement.end_date}</span>
        </div>
        {placement.has_completion_letter && placement.status !== "approved" && (
          <div className="text-xs text-slate-500">Completion letter received — under review.</div>
        )}
        {certUrl && (
          <a href={certUrl} target="_blank" rel="noopener noreferrer" data-testid="track-view-certificate" className="sms-btn-primary text-xs inline-flex items-center gap-1">
            <Certificate size={12} weight="duotone" /> View certificate
          </a>
        )}
      </div>
    </div>
  );
}

function CertificateBlock({ certUrl, studentName, companyName }) {
  const message = `Hi! Please find my placement certificate from ${companyName || "my placement"}: ${certUrl}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mailto = `mailto:?subject=${encodeURIComponent("My Placement Certificate")}&body=${encodeURIComponent(message)}`;
  const copy = async () => { await navigator.clipboard.writeText(certUrl); toast.success("Certificate link copied"); };
  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">
        Congratulations <span className="font-medium text-slate-900">{studentName}</span> — your placement certificate has been issued and is available for download. Share it with future employers and on your social profiles.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a href={certUrl} target="_blank" rel="noopener noreferrer" data-testid="cert-download" className="sms-btn-primary text-xs inline-flex items-center gap-1">
          <FilePdf size={12} weight="duotone" /> Download PDF
        </a>
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="cert-share-whatsapp" className="sms-btn-secondary text-xs inline-flex items-center gap-1 bg-green-50 border-green-200 text-green-800 hover:bg-green-100">
          <WhatsappLogo size={12} weight="duotone" /> Share on WhatsApp
        </a>
        <a href={mailto} data-testid="cert-share-email" className="sms-btn-secondary text-xs inline-flex items-center gap-1">
          <EnvelopeSimple size={12} weight="duotone" /> Email
        </a>
        <button onClick={copy} data-testid="cert-copy-link" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-[var(--sms-primary)] inline-flex items-center gap-1">
          <Copy size={11} weight="bold" /> Copy link
        </button>
      </div>
    </div>
  );
}

// GraduationCap import is required by linter even if unused inline
// eslint-disable-next-line no-unused-vars
const _GraduationCap = GraduationCap;
